import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import AdminJigsawTemplateService, {
    isJigsawTemplateEnabled,
    isJigsawTemplateInvisible,
} from "./AdminJigsawTemplateService";

const DEFAULT_BACKFILL_SIZE = "10";

export default function EditJigsawTemplatePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [savingEnabled, setSavingEnabled] = useState(false);
    const [savingInvisible, setSavingInvisible] = useState(false);
    const [template, setTemplate] = useState(null);
    const [enabled, setEnabled] = useState(true);
    const [invisible, setInvisible] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
    const [backfillAllStories, setBackfillAllStories] = useState(false);
    const [backfillSizeInput, setBackfillSizeInput] = useState(DEFAULT_BACKFILL_SIZE);
    const [backfillSubmitting, setBackfillSubmitting] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!id) {
                setError("Template id required");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            const result = await AdminJigsawTemplateService.getById(id);
            if (cancelled) return;
            if (!result.success) {
                setError(result.error || "Failed to load template");
                setTemplate(null);
            } else {
                const tpl = result.data;
                setTemplate(tpl);
                setEnabled(isJigsawTemplateEnabled(tpl));
                setInvisible(isJigsawTemplateInvisible(tpl));
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleEnabledChange = useCallback(
        async (checked) => {
            if (!id || savingEnabled) return;
            const previous = enabled;
            setEnabled(checked);
            setSavingEnabled(true);
            const result = await AdminJigsawTemplateService.setEnabled(id, checked);
            setSavingEnabled(false);
            if (!result.success) {
                setEnabled(previous);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Failed to update enabled status",
                });
                return;
            }
            setTemplate((prev) => {
                const next = result.data ?? (prev ? { ...prev, enabled: checked } : prev);
                return next;
            });
            if (result.data) {
                setEnabled(isJigsawTemplateEnabled(result.data));
            }
            setSnackbar({
                open: true,
                severity: "success",
                message: checked ? "Template enabled" : "Template disabled",
            });
        },
        [id, enabled, savingEnabled],
    );

    const handleInvisibleChange = useCallback(
        async (checked) => {
            if (!id || savingInvisible) return;
            const previous = invisible;
            setInvisible(checked);
            setSavingInvisible(true);
            const result = await AdminJigsawTemplateService.setInvisible(id, checked);
            setSavingInvisible(false);
            if (!result.success) {
                setInvisible(previous);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Failed to update invisible status",
                });
                return;
            }
            setTemplate((prev) => {
                const next = result.data ?? (prev ? { ...prev, invisible: checked } : prev);
                return next;
            });
            if (result.data) {
                setInvisible(isJigsawTemplateInvisible(result.data));
            }
            setSnackbar({
                open: true,
                severity: "success",
                message: checked ? "Hidden from home jigsaw listing" : "Visible on home jigsaw listing",
            });
        },
        [id, invisible, savingInvisible],
    );

    const handleOpenBackfillDialog = useCallback(() => {
        setBackfillAllStories(false);
        setBackfillSizeInput(DEFAULT_BACKFILL_SIZE);
        setBackfillDialogOpen(true);
    }, []);

    const handleBackfillSubmit = useCallback(async () => {
        if (!id || backfillSubmitting) return;
        setBackfillSubmitting(true);
        const size = backfillAllStories ? null : backfillSizeInput;
        const result = await AdminJigsawTemplateService.ensureStories(id, size);
        setBackfillSubmitting(false);
        setBackfillDialogOpen(false);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Backfill failed",
            });
            return;
        }
        setBackfillResult(result.data);
    }, [id, backfillAllStories, backfillSizeInput, backfillSubmitting]);

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/jigsaw/template")}
                sx={{ mb: 2 }}
            >
                Back to Templates
            </Button>

            <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                Edit Jigsaw Template
            </Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : template ? (
                <Paper sx={{ p: 3 }}>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Name
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {template.name || "—"}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {template.tileSize ? (
                                <Chip size="small" label={`${template.tileSize}×${template.tileSize}`} />
                            ) : null}
                            {template.createdAt ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={new Date(template.createdAt).toLocaleString()}
                                />
                            ) : null}
                        </Stack>

                        {template.detail && Object.keys(template.detail).length > 0 ? (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Detail
                                </Typography>
                                <Typography
                                    variant="caption"
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        p: 1.5,
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                        overflow: "auto",
                                        fontFamily: "monospace",
                                    }}
                                >
                                    {JSON.stringify(template.detail, null, 2)}
                                </Typography>
                            </Box>
                        ) : null}

                        <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enabled}
                                        onChange={(e) => void handleEnabledChange(e.target.checked)}
                                        disabled={savingEnabled}
                                    />
                                }
                                label="Enabled"
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5 }}>
                                When disabled, this template is hidden from public jigsaw browse and play. Use{" "}
                                <strong>Create puzzles on existing stories</strong> and preview generation while
                                disabled, then enable when everything is ready to expose.
                            </Typography>
                        </Box>

                        <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={invisible}
                                        onChange={(e) => void handleInvisibleChange(e.target.checked)}
                                        disabled={savingInvisible}
                                    />
                                }
                                label="Invisible on home"
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5 }}>
                                When checked, this template is omitted from <strong>/home/jigsaw</strong> even if
                                enabled. Use for large boards (e.g. 32×32) that should not appear in the public
                                catalog. Admin listing still shows this template.
                            </Typography>
                        </Box>

                        <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Create puzzles on existing stories
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 640 }}>
                                Adds a <strong>jigsaw</strong> row for this template on eligible{" "}
                                <strong>REVIEWED</strong> / <strong>PUBLISHED</strong> stories that already have jigsaw
                                puzzles but are missing this template. Works while the template is{" "}
                                <strong>disabled</strong> so you can prepare rows and previews before enabling. Safe to
                                run more than once. Run <strong>Generate preview</strong> on jigsaw browse/detail
                                afterward, then enable this template to expose puzzles to players.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleOpenBackfillDialog}
                                disabled={backfillSubmitting}
                            >
                                Create puzzles on existing stories
                            </Button>
                            {!enabled ? (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Hidden from players until enabled — backfill and previews first, then turn{" "}
                                    <strong>Enabled</strong> on.
                                </Typography>
                            ) : null}
                        </Box>
                    </Stack>
                </Paper>
            ) : null}

            <Dialog
                open={backfillDialogOpen}
                onClose={() => {
                    if (!backfillSubmitting) setBackfillDialogOpen(false);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Backfill template onto stories</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Creates missing jigsaw rows for <strong>{template?.name || id}</strong> on matching stories.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={backfillAllStories}
                                onChange={(e) => setBackfillAllStories(e.target.checked)}
                                disabled={backfillSubmitting}
                            />
                        }
                        label="Process all matching stories"
                    />
                    {!backfillAllStories ? (
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Batch size"
                            type="number"
                            fullWidth
                            value={backfillSizeInput}
                            onChange={(e) => setBackfillSizeInput(e.target.value)}
                            disabled={backfillSubmitting}
                            slotProps={{ htmlInput: { min: 1, max: 1000 } }}
                            helperText="Maximum stories to process this call (1–1000)."
                            sx={{ mt: 1 }}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, pl: 0.5 }}>
                            No size limit — every eligible story missing this template will be processed.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBackfillDialogOpen(false)} disabled={backfillSubmitting}>
                        Cancel
                    </Button>
                    <Button onClick={() => void handleBackfillSubmit()} variant="contained" disabled={backfillSubmitting}>
                        {backfillSubmitting ? <CircularProgress size={22} /> : "Create puzzles"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(backfillResult)}
                onClose={() => setBackfillResult(null)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>Backfill result</DialogTitle>
                <DialogContent dividers>
                    {backfillResult ? (
                        <Stack spacing={1.5}>
                            <Typography variant="body2">
                                <strong>Candidates:</strong> {backfillResult.candidates ?? "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Processed:</strong> {backfillResult.processed ?? "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Created:</strong> {backfillResult.created ?? "—"}
                            </Typography>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Results
                                </Typography>
                                {Array.isArray(backfillResult.results) && backfillResult.results.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {backfillResult.results.map((row, idx) => (
                                            <Typography
                                                component="li"
                                                variant="body2"
                                                key={`backfill-${row?.storyId}-${row?.jigsawId}-${idx}`}
                                            >
                                                {row?.storyId ?? "—"}
                                                {row?.jigsawId ? ` — jigsaw: ${row.jigsawId}` : ""}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No new rows created.
                                    </Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Errors
                                </Typography>
                                {Array.isArray(backfillResult.errors) && backfillResult.errors.length > 0 ? (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {backfillResult.errors.map((err, idx) => (
                                            <Typography
                                                component="li"
                                                variant="body2"
                                                color="error"
                                                key={`backfill-err-${idx}`}
                                            >
                                                {String(err)}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        None
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBackfillResult(null)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(o) => setSnackbar((s) => ({ ...s, open: o }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Container>
    );
}
